import React, { useState, useEffect } from 'react';
import styled from 'styled-components';

import Text from '../../basic/Text';
import theme from '../../../styles/theme';
import Arrow from './../components/RotatingArrow';
import Link from '../../util/link';
import HTMLWrapper from '../../basic/HTMLWrapper';

const {
	font,
	colors,
	breakPoints: { screenQuery },
} = theme;

const Wrapper = styled.div`
	padding-bottom: 50px;
	min-height: calc(100vh);
	max-height: 100%;
	width: 100%;
	visibility: ${props => (props.open ? 'visible' : 'hidden')};
	opacity: ${props => (props.open ? 1 : 0)};
	transition: all 0.6s ease;
	z-index: ${theme.intercomZIndex + 2};
	background-color: ${colors.grayscale.white};
	overflow-y: scroll;
	display: none;
	${screenQuery.large} {
		display: block;
	}
`;

const Section = styled.div``;
const Margin = styled.div`
	padding: 0 10%;
`;
const TopLevel = styled(Text)`
	:hover {
		cursor: pointer;
	}
	/* max-width: 300px; */
	margin: 0 auto !important;
	padding: 15px 0;
	font-size: 14px;
`;
const Slider = styled.div`
	overflow: hidden;
	transition: all 0.75s;
	${props =>
		props.open
			? `max-height: 200vh;
        padding: 20px 0;`
			: `max-height: 0px;`}
	background: ${colors.grayscale.light};
	/* box-shadow: rgba(0, 0, 0, 0.2) 0px 1px 6px 0px inset; */
`;

const SalesDemo = styled.div`
	background-color: ${colors.secondary.lighter};
	position: fixed;
	z-index: 2147483002;
	width: 100%;
	display: flex;
	flex-wrap: nowrap;
	justify-content: space-evenly;
	padding: 15px 0;
	transition-duration: 1s;
	right: 0;
	${props => (props.open ? `bottom: 0;` : `bottom: -100%;`)}
	a:hover, a:active {
		color: ${colors.grayscale.main};
		text-shadow: 0px 0px 0.5px ${colors.grayscale.main};
	}
	* {
		margin: 0;
		font-family: ${theme.font.semiBold};
	}
	${screenQuery.mobile} {
		flex-wrap: wrap;
	}
`;

const MobileMenuLink = styled(Link)`
	padding: 0.5rem;

	div {
		font-size: 14px;
	}
`;

const SubmenuSection = ({ items, noImage }) => {
	return (
		<Section>
			{items.map((item, index) => {
				const slug =
					(item.slug && `${item.prependSlug || '/solutions/'}${item.slug}`) ||
					item.linkUrl;
				let image, type;
				if (!noImage) {
					if (item.linkImage?.file) {
						image = item.mainIcon?.file.url;
						type = 'linkIcon';
					}
					if (
						item.linkMainText ===
						'Watch an on-demand demo of the Submittable platform'
					) {
						image = item.linkImage?.file.url;
						type = 'fullImage';
					}
					if (item.featureItem) {
						type = 'featureImage';
					}
				}

				return (
					<MobileMenuLink
						key={item.contentful_id}
						to={item.linkUrl}
						openInNewTab
					>
						<div>
							{item.linkMainText && (
								<Text component="b5" className="mobile-menu__link" semiBold>
									{item.linkMainText}
								</Text>
							)}
							{item.linkSubTextLong && (
								<HTMLWrapper
									html={item?.linkSubTextLong?.childMarkdownRemark?.html}
									color={colors.black}
								/>
							)}
						</div>
					</MobileMenuLink>
				);
			})}
		</Section>
	);
};

const SubmenuWrapper = styled.div`
	.mobile-menu__link-header {
		font-family: ${font.bold};
	}
`;

const Submenu = ({ section, index }) => {
	const sectionHead = section.menuTitle;

	return (
		<SubmenuWrapper css={{ padding: '0 10%', marginBottom: 10 }}>
			{sectionHead ? (
				<div>
					<Text
						component="b5"
						className="mobile-menu__link-header"
						css={{ margin: '24px 0' }}
					>
						{section.menuTitle}
					</Text>
					<SubmenuSection items={section.menuItems} noImage={index === 0} />
				</div>
			) : (
				<SubmenuSection items={section.menuItems} />
			)}
		</SubmenuWrapper>
	);
};

export default ({
	menu,
	productInnerMenu,
	toggle,
	open,
	noFooterMenu,
	className,
	...props
}) => {
	const [openIndex, setOpenIndex] = useState(null);
	const handleClick = index => {
		if (index === openIndex) {
			setOpenIndex(null);
		} else {
			setOpenIndex(index);
		}
	};

	return (
		<Wrapper open={open} className={className}>
			{menu?.map((topLevel, index) => {
				const title = topLevel.menuTitle || topLevel.linkMainText;

				let columns;
				// a submenu is a title and some menu items.
				// if it's not product, columns are Menu Items
				columns = topLevel.menuItems;

				return (
					<div key={`title-${title}`}>
						<Margin>
							{topLevel.menuItems ? (
								<TopLevel
									css={{ margin: '10px 0' }}
									onClick={() => handleClick(index)}
									semiBold
									component="b5"
								>
									{title}{' '}
									{topLevel.menuItems && (
										<Arrow
											rotating={openIndex === index}
											css={{ float: 'right', width: 25 }}
										/>
									)}
								</TopLevel>
							) : (
								<Link
									onClick={toggle}
									to={topLevel.linkUrl}
									anchorLink={topLevel.anchorLink}
								>
									<TopLevel semiBold css={{ margin: '10px 0' }} component="b5">
										{title}
									</TopLevel>
								</Link>
							)}
						</Margin>
						{columns && (
							<Slider open={openIndex === index}>
								{columns.map((column, index) => (
									<Submenu
										index={index}
										key={`menu-column-${columns[index].contentful_id || columns[index].menuTitle}`}
										toggle={toggle}
										section={column}
									/>
								))}
							</Slider>
						)}
					</div>
				);
			})}

			{!noFooterMenu && (
				<SalesDemo open={open}>
					<Text component="b4">
						<Link color="black" to="/contact-sales/">
							Talk to an expert
						</Link>
					</Text>
					<Text component="b4">|</Text>
					<Text component="b4">
						<Link color="black" to="/demo-video/">
							View Demo
						</Link>
					</Text>
					<Text component="b4">|</Text>
					<Text component="b4">
						<Link color="black" to="https://manager.submittable.com/">
							My account
						</Link>
					</Text>
				</SalesDemo>
			)}
		</Wrapper>
	);
};
